import React, { useState, useEffect } from 'react';
import { getObstacleIcon } from '../../utils/markers';
import { T } from '../../assets/theme';

const groupBy = (arr, keyFn) => {
    return arr.reduce((acc, item) => {
        const key = keyFn(item);
        if (!acc[key]) acc[key] = [];
        acc[key].push(item);
        return acc;
    }, {});
};

function ProgressiveLoader() {
    const msgs = [
        'LOADING AIRPORTS...',
        'LOADING CNS...',
        'LOADING RMAC...',
        'LOADING FLIGHT PROCEDURES...',
        'COMPILING RESULTS...'
    ];
    const [idx, setIdx] = useState(0);

    useEffect(() => {
        const timer = setInterval(() => {
            setIdx(curr => Math.min(curr + 1, msgs.length - 1));
        }, 1200);
        return () => clearInterval(timer);
    }, []);

    return (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', flexDirection: 'column', gap: 16, color: T.white }}>
            <div style={{ fontSize: 32, animation: 'spin 1s linear infinite' }}>⟳</div>
            <div style={{ fontSize: 13, color: T.muted, fontWeight: 800, letterSpacing: '0.05em' }}>{msgs[idx]}</div>
            <style>{`@keyframes spin{ to { transform: rotate(360deg); } }`}</style>
        </div>
    );
}

const DEG = Math.PI / 180;
function haversine(lat1, lon1, lat2, lon2) {
    const R = 6371000;
    const dLat = (lat2 - lat1) * DEG, dLon = (lon2 - lon1) * DEG;
    const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * DEG) * Math.cos(lat2 * DEG) * Math.sin(dLon / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

const PROCS = [
    { key: 'standard', label: 'Standard SID (5.0%)', gradient: 5.0, moc: 90 },
    { key: 'low', label: 'Low SID (3.2%)', gradient: 3.2, moc: 75 },
    { key: 'lnav', label: 'LNAV (2.4%)', gradient: 2.4, moc: 75 },
    { key: 'ois', label: 'OIS (0.8%)', gradient: 0.8, moc: 0 },
];

const BRA_MAP = {
    VOR: { j: 600, cylH: 90, alpha: 2, coneR: 10000 }, DVOR: { j: 1000, cylH: 90, alpha: 2, coneR: 12000 },
    DME: { j: 600, cylH: 90, alpha: 2, coneR: 10000 }, NDB: { j: 300, cylH: 30, alpha: 2, coneR: 5000 },
};
function getBRA(type) {
    for (const [k, v] of Object.entries(BRA_MAP)) { if ((type || '').toUpperCase().includes(k)) return v; }
    return BRA_MAP.NDB;
}

const exportToCSV = (data, filename) => {
    if (!data.length) return;
    const headers = Object.keys(data[0]);
    const csvContent = [
        headers.join(','),
        ...data.map(row => headers.map(h => {
            const val = row[h] === null || row[h] === undefined ? '' : row[h];
            const str = String(val).replace(/"/g, '""');
            return `"${str}"`;
        }).join(','))
    ].join('\n');

    const blob = new Blob(["\ufeff" + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};

function toDMS(deg, isLat) {
    if (deg == null || isNaN(deg)) return '—';
    const abs = Math.abs(deg);
    const d = Math.floor(abs);
    const mFull = (abs - d) * 60;
    const m = Math.floor(mFull);
    const s = ((mFull - m) * 60).toFixed(4);
    const dir = isLat ? (deg >= 0 ? 'N' : 'S') : (deg >= 0 ? 'E' : 'W');
    return `${d}° ${m}' ${s}" ${dir}`;
}

// Minor ≤ 5 m, Major > 5 m — blank for non-penetrating rows.
function penetrationComment(status, penetrationM) {
    if (status !== 'penetrated' || penetrationM == null) return '';
    return penetrationM <= 5 ? 'Minor' : 'Major';
}

function stat(s) {
    if (s === 'penetrated' || s === 'YES') return { bg: 'rgba(232,64,64,0.18)', txt: T.danger, label: s === 'YES' ? '⚡ INFRINGED' : '⚡ PENETRATED' };
    if (s === 'CNS Engineer') return { bg: 'rgba(245,166,35,0.18)', txt: '#f5a623', label: '⚠ CNS ENGINEER' };
    if (s === 'not penetrated' || s === 'clear' || s === 'No') return { bg: 'rgba(46,201,139,0.14)', txt: T.safe, label: s === 'No' ? '✔ OK' : '✔ NOT PENETRATED' };
    return { bg: T.badgeBg, txt: T.muted, label: s === 'outside surfaces' ? '— OUTSIDE SURFACES' : (s || '—').toUpperCase() };
}

function Badge({ s }) {
    const { bg, txt, label } = stat(s);
    return <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 10, fontSize: 10, fontWeight: 700, color: txt, background: bg }}>{label}</span>;
}

function ObstacleBalloon({ obstacle, onClose }) {
    if (!obstacle) return null;
    const rows = [
        ['Type', obstacle.obstacle_type],
        ['Latitude', obstacle.latitude != null ? Number(obstacle.latitude).toFixed(7) : null],
        ['Longitude', obstacle.longitude != null ? Number(obstacle.longitude).toFixed(7) : null],
        ['Elevation', obstacle.elevation_m != null ? `${obstacle.elevation_m} m` : null],
        ['H Tolerance', obstacle.h_tolerance != null ? `${obstacle.h_tolerance} m` : null],
        ['V Tolerance', obstacle.v_tolerance != null ? `${obstacle.v_tolerance} m` : null],
        ['Description', obstacle.description],
    ].filter(([, v]) => v != null && v !== '');
    return (
        <div style={{
            position: 'fixed', top: 80, right: 20, zIndex: 2000,
            background: '#1e2330', border: '1px solid rgba(255,255,255,0.12)',
            borderRadius: 12, padding: '14px 16px', width: 260,
            boxShadow: '0 8px 32px rgba(0,0,0,0.55)',
            pointerEvents: 'all',
        }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
                <div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#fff', lineHeight: 1.3 }}>{obstacle.name || '—'}</div>
                    <div style={{ fontSize: 10, color: T.muted, marginTop: 2 }}>ID {obstacle.id}</div>
                </div>
                <button onClick={onClose} style={{ background: 'none', border: 'none', color: T.muted, cursor: 'pointer', fontSize: 18, lineHeight: 1, padding: '0 0 0 8px', flexShrink: 0 }}>✕</button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '5px 10px', fontSize: 11 }}>
                {rows.map(([label, val]) => (
                    <React.Fragment key={label}>
                        <span style={{ color: T.muted, whiteSpace: 'nowrap' }}>{label}</span>
                        <span style={{ color: '#fff', fontFamily: label === 'Latitude' || label === 'Longitude' ? 'monospace' : 'inherit', wordBreak: 'break-all' }}>{val}</span>
                    </React.Fragment>
                ))}
            </div>
        </div>
    );
}

// Generic grouped-results table — pill buttons to pick a group (aerodrome,
// navaid, procedure, ...) + a table for that group's rows + CSV export.
// Powers every result tab added beyond the original OLS/BRA/PANS-OPS three,
// which keep their own bespoke rendering above.
function GroupedResultsView({ groups, columns, pillColor, csvPrefix, activeGroup, setActiveGroup, emptyMessage, onObstacleClick, csvColumns }) {
    if (!groups.length) {
        return <div style={{ padding: 20, color: T.muted, fontSize: 12 }}>{emptyMessage || 'No results.'}</div>;
    }
    const currentKey = (activeGroup === 'ALL' || !groups.some(g => g.key === activeGroup)) ? groups[0].key : activeGroup;
    const current = groups.find(g => g.key === currentKey);
    const rows = current?.rows || [];
    // csvColumns lets a caller export under custom header labels (e.g. "Obstacle
    // ID" instead of the row's internal `obstacle` key); without it, CSV headers
    // fall back to the row objects' own keys, same as before.
    const exportRows = csvColumns
        ? rows.map(r => Object.fromEntries(csvColumns.map(c => [c.label, c.value(r)])))
        : rows.map(({ _raw, _penetrated, ...r }) => r);

    return (
        <div style={{ padding: '0 20px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '20px 0 12px' }}>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {groups.map(g => (
                        <button
                            key={g.key}
                            onClick={() => setActiveGroup(g.key)}
                            style={{
                                padding: '6px 14px', borderRadius: 20,
                                background: currentKey === g.key ? pillColor : 'rgba(255,255,255,0.05)',
                                color: currentKey === g.key ? '#fff' : T.muted,
                                border: 'none', fontSize: 11, fontWeight: 700, cursor: 'pointer',
                            }}
                        >
                            {g.label}
                        </button>
                    ))}
                </div>
                <button
                    onClick={() => exportToCSV(exportRows, `${csvPrefix}_${currentKey}.csv`)}
                    style={{ padding: '6px 14px', borderRadius: 6, background: 'rgba(46,201,139,0.1)', color: T.safe, border: `1px solid ${T.safe}44`, fontSize: 11, fontWeight: 700, cursor: 'pointer' }}
                >
                    💾 Export CSV
                </button>
            </div>

            <div style={{ overflowX: 'auto', width: '100%' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11, whiteSpace: 'nowrap' }}>
                    <thead>
                        <tr style={{ background: T.bg1, position: 'sticky', top: 0 }}>
                            {columns.map(c => (
                                <th key={c.key} style={{ padding: '10px 12px', textAlign: 'center', color: T.muted, fontWeight: 600, borderBottom: `1px solid ${T.border}` }}>{c.label}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((r, i) => (
                            <tr
                                key={i}
                                onClick={() => onObstacleClick?.(r.obstacle)}
                                style={{
                                    borderBottom: `1px solid ${T.border}11`,
                                    background: r._penetrated ? 'rgba(232,64,64,0.05)' : 'transparent',
                                    cursor: onObstacleClick ? 'pointer' : 'default',
                                }}
                            >
                                {columns.map(c => (
                                    <td key={c.key} style={{ padding: '8px 12px', color: T.white, textAlign: 'center' }}>{c.render(r)}</td>
                                ))}
                            </tr>
                        ))}
                        {rows.length === 0 && (
                            <tr><td colSpan={columns.length} style={{ padding: 20, color: T.muted, textAlign: 'center' }}>No results for this group.</td></tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

export default function ResultsPage({ project, assessAll, loading, onRun, depOisResults, arrOisResult, airspaceResult, olsAdgResults }) {
    const [view, setView] = useState('ols');
    const [activeGroup, setActiveGroup] = useState('ALL');
    const [selectedObstacle, setSelectedObstacle] = useState(null);

    // Reset sub-tab when main tab changes
    useEffect(() => {
        setActiveGroup('ALL');
    }, [view]);

    if (loading) return <ProgressiveLoader />;

    if (!assessAll) return (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', flexDirection: 'column', gap: 16 }}>
            <div style={{ fontSize: 48 }}>📊</div>
            <div style={{ fontSize: 16, fontWeight: 700, color: T.white }}>No assessment data yet</div>
            <div style={{ fontSize: 13, color: T.muted }}>Click "Run Assessment" to compute OLS, BRA and PANS-OPS results for all obstacles.</div>
            <button onClick={onRun} style={{ marginTop: 8, padding: '10px 28px', borderRadius: 8, border: `1px solid ${T.safe}55`, background: 'rgba(46,201,139,0.15)', color: T.safe, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                ▶ Run Assessment
            </button>
        </div>
    );

    const { obstacles, ols_results, bra_results, pans_ops_results } = assessAll;

    const obstacleByName = {};
    (obstacles || []).forEach(o => { obstacleByName[o.name] = o; });
    const selectObstacle = name => setSelectedObstacle(obstacleByName[name] || null);

    // ── Summary cards ─────────────────────────────────────────────────────────
    const olsPenetrated = ols_results.filter(r => r.overall_status === 'penetrated').length;
    const braPenetrated = bra_results.filter(r => r.outcome === 'penetrated' || r.outcome === 'CNS Engineer').length;
    const pansPenetrated = pans_ops_results.filter(r => r.status === 'penetrated').length;

    const summaryCards = [
        { icon: '⛱', label: 'Obstacles', val: obstacles.length, sub: 'total in project', color: '#3a8ac4' },
        { icon: '✈', label: 'OLS Checks', val: ols_results.length, sub: `${olsPenetrated} penetrated`, color: olsPenetrated > 0 ? T.danger : T.safe },
        { icon: '📡', label: 'BRA Checks', val: bra_results.length, sub: `${braPenetrated} penetrated`, color: braPenetrated > 0 ? T.danger : T.safe },
        { icon: '🛫', label: 'PANS-OPS Checks', val: pans_ops_results.length, sub: `${pansPenetrated} penetrated`, color: pansPenetrated > 0 ? T.danger : T.safe },
    ];

    // ── RMAC — one entry per aerodrome already, just relabel fields ──────────
    const rmacGroups = (assessAll.rmac_results || []).map(g => ({
        key: g.aerodrome, label: g.aerodrome,
        rows: (g.results || []).map(r => ({
            obstacle: r.obstacle_name, sector: r.sector_name,
            moca_ft: r.moca_ft, total_elev_m: r.total_elevation_m,
            bearing: r.bearing_deg, distance_nm: r.distance_nm, diff_ft: r.result_diff,
            status: r.is_penetration ? 'penetrated' : 'clear',
            _penetrated: !!r.is_penetration,
        })),
    }));

    // ── MSA — one entry per (aerodrome, navaid) ───────────────────────────────
    const msaGroups = (assessAll.msa_results || []).map(g => ({
        key: `${g.aerodrome}_${g.navaid}`, label: `${g.aerodrome} (${g.navaid})`,
        rows: (g.results || []).map(r => ({
            obstacle: r.obstacle_name, elev_m: r.obstacle_elev_m,
            bearing: r.bearing, dist_nm: r.dist_nm, total_elev_ft: r.total_elev_ft,
            status: r.penetrates ? 'penetrated' : 'clear',
            _penetrated: !!r.penetrates,
        })),
    }));

    // ── Circling — flatten one row per (obstacle, category) ──────────────────
    const circlingGroups = (assessAll.circling_results || []).map(g => ({
        key: g.aerodrome, label: g.aerodrome,
        rows: (g.results || []).flatMap(r => (r.evaluations || []).map(e => ({
            obstacle: r.obstacle_name, category: e.category,
            moc_ft: e.moc_applied_ft, total_elev_ft: e.total_elev_ft,
            minima_ft: e.category_minima_ft, penetration_ft: e.penetration_ft,
            status: e.penetrates ? 'penetrated' : 'clear',
            _penetrated: !!e.penetrates,
        }))),
    }));

    // ── NPA — keyed by ICAO ───────────────────────────────────────────────────
    const npaGroups = Object.entries(assessAll.npa_results || {}).map(([icao, data]) => ({
        key: icao, label: icao,
        rows: (data.results || []).map(r => ({
            obstacle: r.obstacle_name, rwy_pair: r.rwy_pair, direction: r.approach_direction,
            elev_ft: r.obstacle_elev_ft, oca_ft: r.oca_ft, margin_ft: r.margin_ft,
            status: r.status || (r.penetrates ? 'penetrated' : 'clear'),
            _penetrated: !!r.penetrates,
        })),
    }));

    // ── VSS — keyed by ICAO ────────────────────────────────────────────────────
    const vssGroups = Object.entries(assessAll.vss_results || {}).map(([icao, data]) => ({
        key: icao, label: icao,
        rows: (data.results || []).map(r => ({
            obstacle: r.obstacle_name, designator: r.designator,
            obs_elev_m: r.obstacle_elev_m, vss_elev_m: r.vss_elev_m, penetration_m: r.penetration_m,
            status: r.ignored ? 'outside surfaces' : (r.penetrates ? 'penetrated' : 'clear'),
            _penetrated: !!r.penetrates,
        })),
    }));

    // ── Departure OIS — keyed by ICAO, lifted from its own tab's local state ──
    const depOisGroups = Object.entries(depOisResults || {}).map(([icao, entry]) => ({
        key: icao, label: icao,
        rows: (entry?.data?.results || []).map(r => ({
            obstacle: r.obstacle_name, runway: r.runway,
            surface_elev_m: r.surface_elev_m, pdg_pct: r.pdg_percent, result: r.result,
            status: r.penetrates ? 'penetrated' : 'clear',
            _penetrated: !!r.penetrates,
        })),
    }));

    // ── Arrival OIS — grouped by procedure (project-wide, not per-ICAO) ───────
    const arrOisGroups = (arrOisResult?.procedures || []).map(p => ({
        key: String(p.id ?? p.name), label: `${p.name}${p.runway ? ` (RWY ${p.runway})` : ''}`,
        rows: (p.results || []).map(r => {
            const penetrated = r.verdict === 'IMPACT' || r.clears === false;
            return {
                obstacle: r.obstacle_name, segment: r.segment, area: r.area_type,
                floor_m: r.floor_m, margin_m: r.margin_m, verdict: r.verdict,
                status: penetrated ? 'penetrated' : 'clear',
                _penetrated: penetrated,
            };
        }),
    }));

    // ── Airspace — single airspace result, one pseudo-group ──────────────────
    const airspaceGroups = airspaceResult ? [{
        key: airspaceResult.airspace_name || 'airspace', label: airspaceResult.airspace_name || 'Airspace',
        rows: (airspaceResult.results || []).map(r => ({
            obstacle: r.obstacle_name, elev_m: r.obstacle_elev_m,
            inside: r.is_inside ? 'Yes' : 'No', nearest: r.is_nearest ? 'Yes' : 'No',
            distance_m: r.distance_m, vert_clear_m: r.vertical_clearance_m,
            status: r.is_inside ? 'penetrated' : 'clear',
            _penetrated: !!r.is_inside,
        })),
    }] : [];

    // ── OLS (ADG) — primarily from the central assess-all run (covers every
    // aerodrome near an obstacle automatically); the per-tab lifted state is
    // merged in as a fallback for any aerodrome assessed manually that the
    // central pass didn't reach (e.g. one with no obstacles nearby) ─────────
    const olsAdgMap = {};
    (assessAll.ols_adg_results || []).forEach(g => { olsAdgMap[g.aerodrome] = g.results || []; });
    Object.entries(olsAdgResults || {}).forEach(([icao, rows]) => {
        if (!olsAdgMap[icao]) olsAdgMap[icao] = rows || [];
    });
    const olsAdgObsLookup = {};
    obstacles.forEach(o => { olsAdgObsLookup[o.id] = o; });
    const olsAdgGroups = Object.entries(olsAdgMap).map(([icao, rows]) => ({
        key: icao, label: icao,
        rows: (rows || []).map(r => {
            const obs = olsAdgObsLookup[r.obstacle_id];
            const penetration = r.margin != null ? parseFloat((-r.margin).toFixed(2)) : null;
            return {
                obstacle: r.obstacle_name, lat: obs?.latitude, lon: obs?.longitude, elev: obs?.elevation_m,
                runway: r.runway, surface: r.surface,
                surf_elev_amsl: r.surf_elev_amsl, obs_elev: r.obs_elev, margin: r.margin,
                penetration, status: r.status,
                comments: penetrationComment(r.status, penetration),
                _penetrated: r.status === 'penetrated',
            };
        }),
    }));

    const tabs = [
        { key: 'ols', label: `✈ OLS (${ols_results.length})` },
        { key: 'ols-adg', label: `🚧 OLS ADG (${Object.values(olsAdgMap).reduce((n, r) => n + r.length, 0)})` },
        { key: 'bra', label: `📡 BRA (${bra_results.length})` },
        { key: 'pans', label: `🛫 PANS-OPS (${pans_ops_results.length})` },
        { key: 'rmac', label: `🛬 RMAC (${rmacGroups.reduce((n, g) => n + g.rows.length, 0)})` },
        { key: 'msa', label: `🏔 MSA (${msaGroups.reduce((n, g) => n + g.rows.length, 0)})` },
        { key: 'circling', label: `🔄 Circling (${circlingGroups.reduce((n, g) => n + g.rows.length, 0)})` },
        { key: 'npa', label: `🛬 NPA (${npaGroups.reduce((n, g) => n + g.rows.length, 0)})` },
        { key: 'vss', label: `👁️ VSS (${vssGroups.reduce((n, g) => n + g.rows.length, 0)})` },
        { key: 'dep-ois', label: `🛫 SID OIS (${depOisGroups.reduce((n, g) => n + g.rows.length, 0)})` },
        { key: 'arr-ois', label: `🛬 STAR OIS (${arrOisGroups.reduce((n, g) => n + g.rows.length, 0)})` },
        { key: 'airspace', label: `🛂 Airspace (${airspaceGroups.reduce((n, g) => n + g.rows.length, 0)})` },
        { key: 'consolidated', label: `📋 Consolidated` },
    ];

    // ── Consolidated: one row per obstacle ────────────────────────────────────
    const consolidated = obstacles.map(obs => {
        const olsRows = ols_results.filter(r => r.obstacle_id === obs.id);
        const braRows = bra_results.filter(r => r.obstacle_id === obs.id);
        const pansRows = pans_ops_results.filter(r => r.obstacle_id === obs.id);
        const olsSt = olsRows.some(r => r.overall_status === 'penetrated') ? 'penetrated' : olsRows.length ? 'clear' : 'N/A';
        const braSt = braRows.some(r => r.outcome === 'penetrated' || r.outcome === 'CNS Engineer') ? 'penetrated' : braRows.length ? 'clear' : 'N/A';
        const pansSt = pansRows.some(r => r.status === 'penetrated') ? 'penetrated' : pansRows.length ? 'clear' : 'N/A';
        return { ...obs, olsSt, braSt, pansSt };
    });

    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden', background: T.bg0 }}>
            {/* Summary cards */}
            <div style={{ display: 'flex', gap: 16, padding: '16px 20px', borderBottom: `1px solid ${T.border}`, flexShrink: 0 }}>
                {summaryCards.map(c => (
                    <div key={c.label} style={{ flex: 1, background: T.bg1, borderRadius: 10, padding: '12px 16px', border: `1px solid ${c.color}33` }}>
                        <div style={{ fontSize: 20 }}>{c.icon}</div>
                        <div style={{ fontSize: 24, fontWeight: 900, color: c.color, lineHeight: 1.1 }}>{c.val}</div>
                        <div style={{ fontSize: 11, fontWeight: 700, color: T.white, marginTop: 2 }}>{c.label}</div>
                        <div style={{ fontSize: 10, color: T.muted }}>{c.sub}</div>
                    </div>
                ))}
                <button onClick={() => window.print()} style={{ alignSelf: 'flex-end', padding: '10px 20px', borderRadius: 8, border: `1px solid ${T.border}`, background: T.bg1, color: T.white, fontSize: 12, cursor: 'pointer', fontWeight: 600, whiteSpace: 'nowrap' }}>
                    🖨 Export PDF
                </button>
            </div>

            {/* Sub-tabs */}
            <div style={{ display: 'flex', borderBottom: `1px solid ${T.border}`, padding: '0 20px', flexShrink: 0 }}>
                {tabs.map(t => (
                    <button key={t.key} onClick={() => setView(t.key)} style={{ padding: '8px 16px', background: 'none', border: 'none', borderBottom: view === t.key ? `2px solid ${T.blue}` : '2px solid transparent', color: view === t.key ? T.blue : T.muted, fontSize: 12, fontWeight: view === t.key ? 700 : 400, cursor: 'pointer' }}>{t.label}</button>
                ))}
            </div>

            <div style={{ flex: 1, overflowY: 'auto', padding: '0 0 20px' }}>

                {/* ── OLS Results ── */}
                {view === 'ols' && (() => {
                    const olsObsLookup = {};
                    obstacles.forEach(o => { olsObsLookup[o.id] = o; });

                    const olsGroups = new Map();
                    ols_results.forEach(r => {
                        if (!olsGroups.has(r.aerodrome)) {
                            olsGroups.set(r.aerodrome, { icao: r.aerodrome, name: r.aerodrome_name || r.aerodrome, rows: [] });
                        }
                        const obs = olsObsLookup[r.obstacle_id];
                        (r.surfaces || []).forEach(s => {
                            const pen = s.margin != null ? parseFloat((-s.margin).toFixed(2)) : null;
                            const isGlobal = ['Inner Horizontal', 'Conical', 'Outer Horizontal'].some(name => s.name?.includes(name));
                            olsGroups.get(r.aerodrome).rows.push({
                                obstacle: r.obstacle_name,
                                aerodrome: r.aerodrome,
                                lat: obs?.latitude,
                                lon: obs?.longitude,
                                elev: obs?.elevation_m,
                                runway: isGlobal ? 'ALL' : (s.rwy || '—').replace('—', '-'),
                                surface: s.name,
                                surf_elev_amsl: s.surf_elev_amsl,
                                penetration_m: pen,
                                status: s.status,
                                comments: penetrationComment(s.status, pen),
                            });
                        });
                    });

                    const icaos = Array.from(olsGroups.keys());
                    const currentIcao = activeGroup === 'ALL' ? (icaos[0] || 'ALL') : activeGroup;
                    const groupData = olsGroups.get(currentIcao);

                    return (
                        <div style={{ padding: '0 20px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '20px 0 12px' }}>
                                <div style={{ display: 'flex', gap: 8 }}>
                                    {icaos.map(icao => (
                                        <button 
                                            key={icao} 
                                            onClick={() => setActiveGroup(icao)}
                                            style={{ 
                                                padding: '6px 14px', borderRadius: 20, 
                                                background: currentIcao === icao ? T.blue : 'rgba(255,255,255,0.05)', 
                                                color: currentIcao === icao ? '#fff' : T.muted, 
                                                border: 'none', fontSize: 11, fontWeight: 700, cursor: 'pointer' 
                                            }}
                                        >
                                            {icao}
                                        </button>
                                    ))}
                                </div>
                                <button
                                    onClick={() => exportToCSV((groupData?.rows || []).map(r => ({
                                        'Obstacle ID': r.obstacle,
                                        'Latitude': toDMS(r.lat, true),
                                        'Longitude': toDMS(r.lon, false),
                                        'Elevation (m)': r.elev ?? '',
                                        'Surface': r.surface,
                                        'Surface Elevation (m)': r.surf_elev_amsl != null ? r.surf_elev_amsl.toFixed(2) : '',
                                        'Penetration (m)': r.penetration_m != null ? r.penetration_m.toFixed(2) : '',
                                        'RWY': r.runway,
                                        'Comments': r.comments,
                                    })), `OLS_Results_${currentIcao}.csv`)}
                                    style={{ padding: '6px 14px', borderRadius: 6, background: 'rgba(46,201,139,0.1)', color: T.safe, border: `1px solid ${T.safe}44`, fontSize: 11, fontWeight: 700, cursor: 'pointer' }}
                                >
                                    💾 Export CSV
                                </button>
                            </div>

                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                                <thead>
                                    <tr style={{ background: T.bg1, position: 'sticky', top: 0 }}>
                                        {['Obstacle ID', 'Latitude', 'Longitude', 'Elevation (m)', 'Surface', 'Surface Elevation (m)', 'Penetration (m)', 'RWY', 'Comments'].map(h => (
                                            <th key={h} style={{ padding: '10px 12px', textAlign: 'center', color: T.muted, fontWeight: 600, borderBottom: `1px solid ${T.border}` }}>{h}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {groupData?.rows.map((r, i) => (
                                        <tr key={i} onClick={() => selectObstacle(r.obstacle)} style={{ borderBottom: `1px solid ${T.border}11`, background: r.status === 'penetrated' ? 'rgba(232,64,64,0.05)' : 'transparent', cursor: 'pointer' }}>
                                            <td style={{ padding: '8px 12px', color: T.white, fontWeight: 600, textAlign: 'center' }}>{r.obstacle}</td>
                                            <td style={{ padding: '8px 12px', color: T.white, textAlign: 'center', whiteSpace: 'nowrap' }}>{toDMS(r.lat, true)}</td>
                                            <td style={{ padding: '8px 12px', color: T.white, textAlign: 'center', whiteSpace: 'nowrap' }}>{toDMS(r.lon, false)}</td>
                                            <td style={{ padding: '8px 12px', color: T.white, textAlign: 'center' }}>{r.elev ?? '—'}</td>
                                            <td style={{ padding: '8px 12px', color: T.white, textAlign: 'center' }}>{r.surface}</td>
                                            <td style={{ padding: '8px 12px', color: T.white, textAlign: 'center' }}>{r.surf_elev_amsl != null ? r.surf_elev_amsl.toFixed(2) : '—'}</td>
                                            <td style={{ padding: '8px 12px', color: r.penetration_m == null ? T.muted : r.penetration_m > 0 ? T.danger : T.safe, fontWeight: 700, textAlign: 'center' }}>
                                                {r.penetration_m != null ? r.penetration_m.toFixed(2) : '—'}
                                            </td>
                                            <td style={{ padding: '8px 12px', color: T.muted, textAlign: 'center' }}>{r.runway}</td>
                                            <td style={{ padding: '8px 12px', color: r.comments === 'Major' ? T.danger : r.comments === 'Minor' ? '#f5a623' : T.muted, fontWeight: 700, textAlign: 'center' }}>{r.comments || '—'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    );
                })()}

                {/* ── BRA Results ── */}
                {view === 'bra' && (() => {
                    const braGroups = groupBy(bra_results, r => r.navaid_ident || 'Unknown');
                    const idents = Object.keys(braGroups);
                    const currentIdent = activeGroup === 'ALL' ? (idents[0] || 'Unknown') : activeGroup;
                    const rows = braGroups[currentIdent] || [];

                    return (
                        <div style={{ padding: '0 20px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '20px 0 12px' }}>
                                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                    {idents.map(id => (
                                        <button 
                                            key={id} 
                                            onClick={() => setActiveGroup(id)}
                                            style={{ 
                                                padding: '6px 14px', borderRadius: 20, 
                                                background: currentIdent === id ? '#f5a623' : 'rgba(255,255,255,0.05)', 
                                                color: currentIdent === id ? '#000' : T.muted, 
                                                border: 'none', fontSize: 11, fontWeight: 700, cursor: 'pointer' 
                                            }}
                                        >
                                            {id}
                                        </button>
                                    ))}
                                </div>
                                <button
                                    onClick={() => exportToCSV(
                                        // Flatten bearing out of the nested `params` object first — passed
                                        // through raw, it would stringify as "[object Object]" in the CSV.
                                        rows.map(({ params, ...r }) => ({ ...r, bearing_deg: params?.brng ?? '' })),
                                        `BRA_Results_${currentIdent}.csv`
                                    )}
                                    style={{ padding: '6px 14px', borderRadius: 6, background: 'rgba(245,166,35,0.1)', color: '#f5a623', border: '1px solid #f5a62344', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}
                                >
                                    💾 Export CSV
                                </button>
                            </div>

                            <div style={{ overflowX: 'auto', width: '100%' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11, whiteSpace: 'nowrap' }}>
                                    <thead>
                                        <tr style={{ background: T.bg1, position: 'sticky', top: 0 }}>
                                            {[
                                                'obstacle name', 'obstacle elevation (m)', 'vertical tol (m)', 'horizontal tol (m)',
                                                'cns ident', 'type', 'distance (m)', 'bearing (°t)', 'surface name', 'Penetration (m)', 'outcome'
                                            ].map(h => (
                                                <th key={h} style={{ padding: '10px 12px', textAlign: 'center', color: T.muted, fontWeight: 600, borderBottom: `1px solid ${T.border}` }}>{h}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {rows.map((r, i) => (
                                            <tr key={i} onClick={() => selectObstacle(r.obstacle_name)} style={{ borderBottom: `1px solid ${T.border}11`, background: (r.outcome === 'penetrated' || r.outcome === 'CNS Engineer') ? 'rgba(232,64,64,0.05)' : 'transparent', cursor: 'pointer' }}>
                                                <td style={{ padding: '8px 12px', color: T.white, fontWeight: 600, textAlign: 'center' }}>{r.obstacle_name}</td>
                                                <td style={{ padding: '8px 12px', color: T.white, textAlign: 'center' }}>{r.obstacle_elev}</td>
                                                <td style={{ padding: '8px 12px', color: T.white, textAlign: 'center' }}>{r.vert_tol}</td>
                                                <td style={{ padding: '8px 12px', color: T.white, textAlign: 'center' }}>{r.horiz_tol}</td>
                                                <td style={{ padding: '8px 12px', color: '#f5a623', fontWeight: 700, textAlign: 'center' }}>{r.navaid_ident}</td>
                                                <td style={{ padding: '8px 12px', color: T.white, textAlign: 'center' }}>{r.navaid_type}</td>
                                                <td style={{ padding: '8px 12px', color: T.white, textAlign: 'center' }}>{r.dist_m ?? '—'}</td>
                                                <td style={{ padding: '8px 12px', color: T.white, textAlign: 'center' }}>{r.params?.brng != null ? `${r.params.brng}°` : '—'}</td>
                                                <td style={{ padding: '8px 12px', color: T.white, textAlign: 'center' }}>{r.surface}</td>
                                                <td style={{ padding: '8px 12px', color: T.white, textAlign: 'center' }}>{r.penetration}</td>
                                                <td style={{ padding: '8px 12px', textAlign: 'center' }}><Badge s={r.outcome} /></td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    );
                })()}

                {/* ── PANS-OPS Results ── */}
                {view === 'pans' && (() => {
                    const pansGroups = groupBy(pans_ops_results, r => r.aerodrome || 'Unknown');
                    const icaos = Object.keys(pansGroups);
                    const currentIcao = activeGroup === 'ALL' ? (icaos[0] || 'Unknown') : activeGroup;
                    const rows = pansGroups[currentIcao] || [];

                    return (
                        <div style={{ padding: '0 20px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '20px 0 12px' }}>
                                <div style={{ display: 'flex', gap: 8 }}>
                                    {icaos.map(icao => (
                                        <button 
                                            key={icao} 
                                            onClick={() => setActiveGroup(icao)}
                                            style={{ 
                                                padding: '6px 14px', borderRadius: 20, 
                                                background: currentIcao === icao ? T.blue : 'rgba(255,255,255,0.05)', 
                                                color: currentIcao === icao ? '#fff' : T.muted, 
                                                border: 'none', fontSize: 11, fontWeight: 700, cursor: 'pointer' 
                                            }}
                                        >
                                            {icao}
                                        </button>
                                    ))}
                                </div>
                                <button 
                                    onClick={() => exportToCSV(rows, `PANS_OPS_Results_${currentIcao}.csv`)}
                                    style={{ padding: '6px 14px', borderRadius: 6, background: 'rgba(46,201,139,0.1)', color: T.safe, border: `1px solid ${T.safe}44`, fontSize: 11, fontWeight: 700, cursor: 'pointer' }}
                                >
                                    💾 Export CSV
                                </button>
                            </div>

                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                                <thead>
                                    <tr style={{ background: T.bg1, position: 'sticky', top: 0 }}>
                                        {['Obstacle', 'Procedure', 'Clear H (m)', 'Obs AGL (m)', 'Margin (m)', 'Status'].map(h => (
                                            <th key={h} style={{ padding: '10px 12px', textAlign: 'center', color: T.muted, fontWeight: 600, borderBottom: `1px solid ${T.border}` }}>{h}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map((r, i) => (
                                        <tr key={i} onClick={() => selectObstacle(r.obstacle_name)} style={{ borderBottom: `1px solid ${T.border}11`, background: r.status === 'penetrated' ? 'rgba(232,64,64,0.05)' : 'transparent', cursor: 'pointer' }}>
                                            <td style={{ padding: '8px 12px', color: T.white, fontWeight: 600, textAlign: 'center' }}>{r.obstacle_name}</td>
                                            <td style={{ padding: '8px 12px', color: T.white, textAlign: 'center' }}>{r.procedure}</td>
                                            <td style={{ padding: '8px 12px', color: T.white, textAlign: 'center' }}>{r.clearance_h}</td>
                                            <td style={{ padding: '8px 12px', color: T.white, textAlign: 'center' }}>{r.obs_h_agl}</td>
                                            <td style={{ padding: '8px 12px', color: Number(r.margin) < 0 ? T.danger : T.safe, fontWeight: 700, textAlign: 'center' }}>{r.margin}</td>
                                            <td style={{ padding: '8px 12px', textAlign: 'center' }}><Badge s={r.status} /></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    );
                })()}

                {/* ── OLS (ADG) ── */}
                {view === 'ols-adg' && (
                    <GroupedResultsView
                        groups={olsAdgGroups} activeGroup={activeGroup} setActiveGroup={setActiveGroup}
                        pillColor={T.blue} csvPrefix="OLS_ADG_Results"
                        emptyMessage="No OLS (ADG) results yet — open the OLS ADG tab and assess an aerodrome."
                        onObstacleClick={selectObstacle}
                        columns={[
                            { key: 'obstacle', label: 'Obstacle ID', render: r => <span style={{ fontWeight: 600 }}>{r.obstacle}</span> },
                            { key: 'lat', label: 'Latitude', render: r => <span style={{ whiteSpace: 'nowrap' }}>{toDMS(r.lat, true)}</span> },
                            { key: 'lon', label: 'Longitude', render: r => <span style={{ whiteSpace: 'nowrap' }}>{toDMS(r.lon, false)}</span> },
                            { key: 'elev', label: 'Elevation (m)', render: r => r.elev ?? '—' },
                            { key: 'surface', label: 'Surface', render: r => r.surface || '—' },
                            { key: 'surf_elev_amsl', label: 'Surface Elevation (m)', render: r => r.surf_elev_amsl ?? '—' },
                            { key: 'penetration', label: 'Penetration (m)', render: r => r.penetration != null ? r.penetration.toFixed(2) : '—' },
                            { key: 'runway', label: 'RWY', render: r => r.runway || '—' },
                            { key: 'comments', label: 'Comments', render: r => <span style={{ color: r.comments === 'Major' ? T.danger : r.comments === 'Minor' ? '#f5a623' : T.muted, fontWeight: 700 }}>{r.comments || '—'}</span> },
                        ]}
                        csvColumns={[
                            { label: 'Obstacle ID', value: r => r.obstacle },
                            { label: 'Latitude', value: r => toDMS(r.lat, true) },
                            { label: 'Longitude', value: r => toDMS(r.lon, false) },
                            { label: 'Elevation (m)', value: r => r.elev ?? '' },
                            { label: 'Surface', value: r => r.surface || '' },
                            { label: 'Surface Elevation (m)', value: r => r.surf_elev_amsl != null ? r.surf_elev_amsl.toFixed(2) : '' },
                            { label: 'Penetration (m)', value: r => r.penetration != null ? r.penetration.toFixed(2) : '' },
                            { label: 'RWY', value: r => r.runway || '' },
                            { label: 'Comments', value: r => r.comments || '' },
                        ]}
                    />
                )}

                {/* ── RMAC ── */}
                {view === 'rmac' && (
                    <GroupedResultsView
                        groups={rmacGroups} activeGroup={activeGroup} setActiveGroup={setActiveGroup}
                        pillColor={T.blue} csvPrefix="RMAC_Results"
                        emptyMessage="No RMAC results — assessed automatically as part of Run Assessment for aerodromes with RMAC sectors."
                        onObstacleClick={selectObstacle}
                        columns={[
                            { key: 'obstacle', label: 'Obstacle', render: r => <span style={{ fontWeight: 600 }}>{r.obstacle}</span> },
                            { key: 'sector', label: 'Sector', render: r => r.sector || '—' },
                            { key: 'moca_ft', label: 'MOCA (ft)', render: r => r.moca_ft ?? '—' },
                            { key: 'total_elev_m', label: 'Total Elev (m)', render: r => r.total_elev_m ?? '—' },
                            { key: 'bearing', label: 'Bearing (°)', render: r => r.bearing ?? '—' },
                            { key: 'distance_nm', label: 'Distance (nm)', render: r => r.distance_nm ?? '—' },
                            { key: 'status', label: 'Status', render: r => <Badge s={r.status} /> },
                        ]}
                    />
                )}

                {/* ── MSA ── */}
                {view === 'msa' && (
                    <GroupedResultsView
                        groups={msaGroups} activeGroup={activeGroup} setActiveGroup={setActiveGroup}
                        pillColor={T.blue} csvPrefix="MSA_Results"
                        emptyMessage="No MSA results — assessed automatically as part of Run Assessment for aerodromes with MSA/TAA navaids."
                        onObstacleClick={selectObstacle}
                        columns={[
                            { key: 'obstacle', label: 'Obstacle', render: r => <span style={{ fontWeight: 600 }}>{r.obstacle}</span> },
                            { key: 'elev_m', label: 'Elev (m)', render: r => r.elev_m ?? '—' },
                            { key: 'bearing', label: 'Bearing (°)', render: r => r.bearing ?? '—' },
                            { key: 'dist_nm', label: 'Distance (nm)', render: r => r.dist_nm ?? '—' },
                            { key: 'total_elev_ft', label: 'Total Elev (ft)', render: r => r.total_elev_ft ?? '—' },
                            { key: 'status', label: 'Status', render: r => <Badge s={r.status} /> },
                        ]}
                    />
                )}

                {/* ── Circling ── */}
                {view === 'circling' && (
                    <GroupedResultsView
                        groups={circlingGroups} activeGroup={activeGroup} setActiveGroup={setActiveGroup}
                        pillColor={T.blue} csvPrefix="Circling_Results"
                        emptyMessage="No Circling results — assessed automatically as part of Run Assessment for aerodromes with circling minima."
                        onObstacleClick={selectObstacle}
                        columns={[
                            { key: 'obstacle', label: 'Obstacle', render: r => <span style={{ fontWeight: 600 }}>{r.obstacle}</span> },
                            { key: 'category', label: 'Category', render: r => r.category || '—' },
                            { key: 'moc_ft', label: 'MOC (ft)', render: r => r.moc_ft ?? '—' },
                            { key: 'total_elev_ft', label: 'Total Elev (ft)', render: r => r.total_elev_ft ?? '—' },
                            { key: 'minima_ft', label: 'Minima (ft)', render: r => r.minima_ft ?? '—' },
                            { key: 'penetration_ft', label: 'Penetration (ft)', render: r => r.penetration_ft ?? '—' },
                            { key: 'status', label: 'Status', render: r => <Badge s={r.status} /> },
                        ]}
                    />
                )}

                {/* ── NPA ── */}
                {view === 'npa' && (
                    <GroupedResultsView
                        groups={npaGroups} activeGroup={activeGroup} setActiveGroup={setActiveGroup}
                        pillColor={T.blue} csvPrefix="NPA_Results"
                        emptyMessage="No NPA results — assessed automatically as part of Run Assessment for aerodromes with NPA minima."
                        onObstacleClick={selectObstacle}
                        columns={[
                            { key: 'obstacle', label: 'Obstacle', render: r => <span style={{ fontWeight: 600 }}>{r.obstacle}</span> },
                            { key: 'rwy_pair', label: 'RWY Pair', render: r => r.rwy_pair || '—' },
                            { key: 'direction', label: 'Direction', render: r => r.direction || '—' },
                            { key: 'elev_ft', label: 'Elev (ft)', render: r => r.elev_ft ?? '—' },
                            { key: 'oca_ft', label: 'OCA (ft)', render: r => r.oca_ft ?? '—' },
                            { key: 'margin_ft', label: 'Margin (ft)', render: r => r.margin_ft ?? '—' },
                            { key: 'status', label: 'Status', render: r => <Badge s={r.status} /> },
                        ]}
                    />
                )}

                {/* ── VSS ── */}
                {view === 'vss' && (
                    <GroupedResultsView
                        groups={vssGroups} activeGroup={activeGroup} setActiveGroup={setActiveGroup}
                        pillColor={T.blue} csvPrefix="VSS_Results"
                        emptyMessage="No VSS results — assessed automatically as part of Run Assessment for aerodromes with NPA minima."
                        onObstacleClick={selectObstacle}
                        columns={[
                            { key: 'obstacle', label: 'Obstacle', render: r => <span style={{ fontWeight: 600 }}>{r.obstacle}</span> },
                            { key: 'designator', label: 'Designator', render: r => r.designator || '—' },
                            { key: 'obs_elev_m', label: 'Obs Elev (m)', render: r => r.obs_elev_m ?? '—' },
                            { key: 'vss_elev_m', label: 'VSS Elev (m)', render: r => r.vss_elev_m ?? '—' },
                            { key: 'penetration_m', label: 'Penetration (m)', render: r => r.penetration_m ?? '—' },
                            { key: 'status', label: 'Status', render: r => <Badge s={r.status} /> },
                        ]}
                    />
                )}

                {/* ── Departure OIS (SID) ── */}
                {view === 'dep-ois' && (
                    <GroupedResultsView
                        groups={depOisGroups} activeGroup={activeGroup} setActiveGroup={setActiveGroup}
                        pillColor={T.blue} csvPrefix="Departure_OIS_Results"
                        emptyMessage="No Departure OIS results yet — open the SID OIS tab and assess an aerodrome."
                        onObstacleClick={selectObstacle}
                        columns={[
                            { key: 'obstacle', label: 'Obstacle', render: r => <span style={{ fontWeight: 600 }}>{r.obstacle}</span> },
                            { key: 'runway', label: 'Runway', render: r => r.runway || '—' },
                            { key: 'surface_elev_m', label: 'Surface Elev (m)', render: r => r.surface_elev_m ?? '—' },
                            { key: 'pdg_pct', label: 'PDG (%)', render: r => r.pdg_pct ?? '—' },
                            { key: 'result', label: 'Result', render: r => r.result || '—' },
                            { key: 'status', label: 'Status', render: r => <Badge s={r.status} /> },
                        ]}
                    />
                )}

                {/* ── Arrival OIS (STAR) ── */}
                {view === 'arr-ois' && (
                    <GroupedResultsView
                        groups={arrOisGroups} activeGroup={activeGroup} setActiveGroup={setActiveGroup}
                        pillColor={T.blue} csvPrefix="Arrival_OIS_Results"
                        emptyMessage="No Arrival OIS results yet — open the STAR OIS tab to compute them."
                        onObstacleClick={selectObstacle}
                        columns={[
                            { key: 'obstacle', label: 'Obstacle', render: r => <span style={{ fontWeight: 600 }}>{r.obstacle}</span> },
                            { key: 'segment', label: 'Segment', render: r => r.segment || '—' },
                            { key: 'area', label: 'Area', render: r => r.area || '—' },
                            { key: 'floor_m', label: 'Floor (m)', render: r => r.floor_m ?? '—' },
                            { key: 'margin_m', label: 'Margin (m)', render: r => r.margin_m ?? '—' },
                            { key: 'status', label: 'Status', render: r => <Badge s={r.status} /> },
                        ]}
                    />
                )}

                {/* ── Airspace ── */}
                {view === 'airspace' && (
                    <GroupedResultsView
                        groups={airspaceGroups} activeGroup={activeGroup} setActiveGroup={setActiveGroup}
                        pillColor={T.blue} csvPrefix="Airspace_Results"
                        emptyMessage="No Airspace results yet — open the Airspace tab to compute them."
                        onObstacleClick={selectObstacle}
                        columns={[
                            { key: 'obstacle', label: 'Obstacle', render: r => <span style={{ fontWeight: 600 }}>{r.obstacle}</span> },
                            { key: 'elev_m', label: 'Elev (m)', render: r => r.elev_m ?? '—' },
                            { key: 'inside', label: 'Inside?', render: r => r.inside },
                            { key: 'nearest', label: 'Nearest?', render: r => r.nearest },
                            { key: 'distance_m', label: 'Distance (m)', render: r => r.distance_m ?? '—' },
                            { key: 'vert_clear_m', label: 'Vert. Clearance (m)', render: r => r.vert_clear_m ?? '—' },
                            { key: 'status', label: 'Status', render: r => <Badge s={r.status} /> },
                        ]}
                    />
                )}

                {/* ── Consolidated ── */}
                {view === 'consolidated' && (
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                        <thead>
                            <tr style={{ background: T.bg1, position: 'sticky', top: 0 }}>
                                {['#', 'Obstacle', 'Type', 'Lat', 'Lon', 'Elev (m)', 'OLS', 'BRA', 'PANS-OPS'].map(h => (
                                    <th key={h} style={{ padding: '8px 12px', textAlign: 'center', color: T.muted, fontWeight: 600, borderBottom: `1px solid ${T.border}` }}>{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {consolidated.map((r, i) => (
                                <tr key={i} onClick={() => setSelectedObstacle(r)} style={{ borderBottom: `1px solid ${T.border}11`, cursor: 'pointer' }}>
                                    <td style={{ padding: '6px 12px', color: T.muted, textAlign: 'center' }}>{i + 1}</td>
                                    <td style={{ padding: '6px 12px', color: T.white, fontWeight: 700, textAlign: 'center' }}>{r.name || '—'}</td>
                                    <td style={{ padding: '6px 12px', color: T.white, textAlign: 'center' }}>{r.obstacle_type}</td>
                                    <td style={{ padding: '6px 12px', color: T.white, fontFamily: 'monospace', textAlign: 'center' }}>{r.latitude?.toFixed(5)}</td>
                                    <td style={{ padding: '6px 12px', color: T.white, fontFamily: 'monospace', textAlign: 'center' }}>{r.longitude?.toFixed(5)}</td>
                                    <td style={{ padding: '6px 12px', color: T.white, textAlign: 'center' }}>{r.elevation_m}</td>
                                    <td style={{ padding: '6px 12px', textAlign: 'center' }}><Badge s={r.olsSt} /></td>
                                    <td style={{ padding: '6px 12px', textAlign: 'center' }}><Badge s={r.braSt} /></td>
                                    <td style={{ padding: '6px 12px', textAlign: 'center' }}><Badge s={r.pansSt} /></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            <ObstacleBalloon obstacle={selectedObstacle} onClose={() => setSelectedObstacle(null)} />

            {/* Print CSS */}
            <style>{`
                @media print {
                    body > *:not(#root) { display: none; }
                    * { background: white !important; color: black !important; }
                    table { font-size: 9pt; }
                    th, td { border: 1px solid #ccc !important; padding: 4px 8px !important; }
                    button { display: none !important; }
                }
            `}</style>
        </div>
    );
}
